/**
 * SQLiteConnector — SQLite database resource via sql.js (WASM).
 * Not mountable. Custom operations: query, schema, execute.
 *
 * Uses sql.js — SQLite compiled to WebAssembly. Runs on Node, Bun, and
 * browsers without native compilation or platform-specific binaries.
 *
 * Requires `sql.js` as an optional peer dependency.
 *
 * `options.path` is declaration-supplied, so a `.db` file under the workspace is
 * plausible configuration — which puts it on the rclone FUSE mount inside the
 * agent container. Both the load and the flush are therefore async and
 * deadline-bounded: a synchronous read/write against a wedged mount parks the
 * whole event loop in an uninterruptible kernel wait (issue #454).
 */

import { readFile, writeFile } from "node:fs/promises";
import type {
  ConnectContext,
  ConnectorContent,
  ConnectorDeclaration,
  ConnectorEntry,
  ConnectorHandle,
  ListOptions,
  OperationDescriptor,
  SearchOptions,
  SearchResult,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";
import { withFsDeadline } from "@skaile/workspaces/core";

interface SqliteState {
  db: any; // sql.js Database instance
  dbPath: string;
  readonly: boolean;
}

/** Escape an identifier for safe interpolation inside double quotes. */
function quoteIdent(ident: string): string {
  return ident.replace(/"/g, '""');
}

/**
 * Connector for SQLite databases via sql.js (WASM). Supports in-memory and file-backed databases.
 * Provides query, schema inspection, and execute operations. Requires the `sql.js` optional peer dependency.
 * @docLink packages/factory-assets/concepts#sqlite-connector
 */
export class SQLiteConnector extends AbstractConnector {
  readonly name = "sqlite";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      read: (handle, path) => this._read(handle, path),
      list: (handle, path, options) => this._list(handle, path, options),
      search: (handle, query, options) => this._search(handle, query, options),
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
    };
  }

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    const started = Date.now();
    const dbPath =
      ctx.secrets?.resolve(declaration.auth ?? "") ?? String(declaration.options?.path ?? "");
    if (!dbPath) {
      this.log.error("connect failed: missing path", undefined);
      throw new Error("SQLiteAdapter requires auth (db path) or options.path");
    }
    this.log.info("connect start", { dbPath });

    const sqljsPkg = "sql.js";
    const initSqlJs = await import(/* @vite-ignore */ sqljsPkg).then((m) => m.default ?? m);
    const SQL = await initSqlJs();

    const isReadonly = declaration.access === "read-only";
    let db: any;

    if (dbPath === ":memory:") {
      db = new SQL.Database();
    } else {
      // One read instead of exists-then-read: a second trip to a possibly
      // unresponsive mount buys nothing, and ENOENT is itself the "create it"
      // signal. Anything else — a deadline miss, EACCES — must fail, because
      // opening a blank database would make the disconnect flush overwrite a
      // real file that was merely unreachable.
      let buffer: Buffer | undefined;
      try {
        buffer = await withFsDeadline(`readFile ${dbPath}`, () => readFile(dbPath));
      } catch (err) {
        if ((err as NodeJS.ErrnoException).code !== "ENOENT") {
          this.log.error("connect failed: db path unreadable", err, { dbPath });
          throw err;
        }
        if (isReadonly) {
          this.log.error("connect failed: db file not found", undefined, { dbPath });
          throw new Error(`SQLite database not found: ${dbPath}`);
        }
      }
      db = buffer ? new SQL.Database(buffer) : new SQL.Database();
    }

    this.log.info("connect ok", { dbPath, durationMs: Date.now() - started });

    // Enable WAL mode for file-backed databases (not supported in sql.js but
    // harmless — sql.js operates in-memory and flushes to disk on save)
    if (!isReadonly && dbPath !== ":memory:") {
      try {
        db.run("PRAGMA journal_mode = WAL");
      } catch {
        /* ignored in WASM */
      }
    }

    return this.makeHandle(declaration, { db, dbPath, readonly: isReadonly } satisfies SqliteState);
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    const { db, dbPath, readonly: isReadonly } = handle.state as SqliteState;
    // Persist changes to disk before closing (sql.js is in-memory)
    if (!isReadonly && dbPath !== ":memory:") {
      await this.saveToDisk(db, dbPath);
    }
    db.close();
    handle.status = "disconnected";
  }

  private db(handle: ConnectorHandle): any {
    return (handle.state as SqliteState).db;
  }

  /** sql.js operates in-memory — flush to disk explicitly. Best-effort, but no
   *  longer silent: a dropped flush on a wedged mount is lost data, so it warns. */
  private async saveToDisk(db: any, dbPath: string): Promise<void> {
    try {
      const data = db.export();
      const buffer = Buffer.from(data);
      await withFsDeadline(`writeFile ${dbPath}`, () => writeFile(dbPath, buffer));
    } catch (err) {
      this.log.warn("db flush failed — changes not persisted", {
        dbPath,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /** Run a query that returns rows. sql.js uses stmt.getAsObject() pattern. */
  private queryRows(db: any, sql: string, params?: unknown[]): Record<string, unknown>[] {
    const stmt = db.prepare(sql);
    if (params?.length) stmt.bind(params);
    const rows: Record<string, unknown>[] = [];
    while (stmt.step()) {
      rows.push(stmt.getAsObject());
    }
    stmt.free();
    return rows;
  }

  /** Run a statement that modifies data. Returns changes count. */
  private runStmt(db: any, sql: string, params?: unknown[]): number {
    if (params?.length) {
      db.run(sql, params);
    } else {
      db.run(sql);
    }
    const changes = db.getRowsModified();
    return changes;
  }

  private async _read(handle: ConnectorHandle, path: string): Promise<ConnectorContent | null> {
    const parts = path.split("/");
    if (parts.length === 1) {
      const table = parts[0]!;
      const info = this.queryRows(this.db(handle), `PRAGMA table_info("${quoteIdent(table)}")`);
      if (info.length === 0) return null;
      return { data: JSON.stringify(info, null, 2), contentType: "application/json" };
    }
    const [table, rowid] = parts;
    const rows = this.queryRows(
      this.db(handle),
      `SELECT * FROM "${quoteIdent(table!)}" WHERE rowid = ?`,
      [rowid],
    );
    if (rows.length === 0) return null;
    return { data: JSON.stringify(rows[0], null, 2), contentType: "application/json" };
  }

  private async _list(
    handle: ConnectorHandle,
    path?: string,
    options?: ListOptions,
  ): Promise<ConnectorEntry[]> {
    if (!path) {
      const tables = this.queryRows(
        this.db(handle),
        "SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%' ORDER BY name",
      );
      return tables.map((t: any) => ({
        name: t.name,
        path: t.name,
        type: "directory" as const,
      }));
    }
    const limit = options?.limit ?? 100;
    const rows = this.queryRows(
      this.db(handle),
      `SELECT rowid, * FROM "${quoteIdent(path)}" LIMIT ?`,
      [limit],
    );
    return rows.map((row: any) => ({
      name: String(row.rowid ?? row.id ?? "?"),
      path: `${path}/${row.rowid ?? row.id ?? "?"}`,
      type: "row" as const,
    }));
  }

  private async _search(
    handle: ConnectorHandle,
    query: string,
    options?: SearchOptions,
  ): Promise<SearchResult[]> {
    const tables = await this._list(handle);
    const results: SearchResult[] = [];
    const maxResults = options?.maxResults ?? 50;

    for (const table of tables) {
      if (results.length >= maxResults) break;
      try {
        const info = this.queryRows(
          this.db(handle),
          `PRAGMA table_info("${quoteIdent(table.name)}")`,
        );
        const textCols = info.filter((c: any) => /text|varchar|char/i.test(String(c.type ?? "")));
        if (textCols.length === 0) continue;

        const conditions = textCols
          .map((c: any) => `"${quoteIdent(String(c.name))}" LIKE ?`)
          .join(" OR ");
        const params: unknown[] = textCols.map(() => `%${query}%`);
        params.push(maxResults - results.length);
        const rows = this.queryRows(
          this.db(handle),
          `SELECT rowid, * FROM "${quoteIdent(table.name)}" WHERE ${conditions} LIMIT ?`,
          params,
        );

        for (const row of rows) {
          results.push({
            path: `${table.name}/${row.rowid ?? "?"}`,
            snippet: JSON.stringify(row).slice(0, 200),
          });
        }
      } catch {
        // Skip inaccessible tables
      }
    }
    return results;
  }

  private _describeOperations(handle: ConnectorHandle): OperationDescriptor[] {
    const ops: OperationDescriptor[] = [
      {
        name: "query",
        description: "Execute a read-only SQL query. Returns JSON rows.",
        accessLevel: "read",
        args: [
          { name: "sql", type: "string", required: true, description: "SQL SELECT statement" },
        ],
        examples: [
          'query --sql "SELECT * FROM users LIMIT 10"',
          'query --sql "SELECT count(*) as total FROM orders"',
        ],
      },
      {
        name: "schema",
        description: "Inspect database schema — tables and their columns.",
        accessLevel: "read",
        args: [
          {
            name: "table",
            type: "string",
            required: false,
            description: "Specific table (omit for all tables)",
          },
        ],
        examples: ["schema", "schema --table users"],
      },
    ];

    if (handle.access === "read-write") {
      ops.push({
        name: "execute",
        description:
          "Execute a SQL statement (INSERT, UPDATE, DELETE, CREATE). Returns changes count.",
        accessLevel: "write",
        args: [{ name: "sql", type: "string", required: true, description: "SQL statement" }],
        examples: ["execute --sql \"INSERT INTO users (name) VALUES ('Alice')\""],
      });
    }

    return ops;
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const db = this.db(handle);
    const state = handle.state as SqliteState;

    switch (operation) {
      case "query": {
        const sql = String(args.sql ?? args._positional ?? "");
        if (!sql) return "Error: sql argument is required";
        const rows = this.queryRows(db, sql);
        return JSON.stringify(rows, null, 2);
      }
      case "schema": {
        const table = args.table ? String(args.table) : undefined;
        if (table) {
          const info = this.queryRows(db, `PRAGMA table_info("${quoteIdent(table)}")`);
          return JSON.stringify(info, null, 2);
        }
        const tables = this.queryRows(
          db,
          "SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%' ORDER BY name",
        );
        const result: Record<string, any> = {};
        for (const t of tables) {
          result[t.name as string] = this.queryRows(
            db,
            `PRAGMA table_info("${quoteIdent(String(t.name))}")`,
          );
        }
        return JSON.stringify(result, null, 2);
      }
      case "execute": {
        const sql = String(args.sql ?? args._positional ?? "");
        if (!sql) return "Error: sql argument is required";
        const changes = this.runStmt(db, sql);
        // Persist after mutation
        if (!state.readonly && state.dbPath !== ":memory:") {
          await this.saveToDisk(db, state.dbPath);
        }
        return JSON.stringify({ changes });
      }
      default:
        throw new Error(`SQLiteAdapter has no operation: ${operation}`);
    }
  }
}

/**
 * Creates a new SQLiteConnector instance.
 * @returns Configured SQLiteConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#sqlite-connector-factory
 */
export function createConnector(): SQLiteConnector {
  return new SQLiteConnector();
}
